import {
  type ApplicationConfig,
  ErrorHandler,
  inject,
  isDevMode,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import {
  provideRouter,
  withComponentInputBinding,
  withRouterConfig,
  withViewTransitions,
} from '@angular/router';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideTransloco } from '@jsverse/transloco';

import { routes } from './app.routes';
import { GlobalErrorHandler } from './app.errors';
import { TranslocoHttpLoader } from './transloco-loader';
import { correlationIdInterceptor } from '@shared/http/interceptors/correlation-id.interceptor';
import { authInterceptor } from '@shared/http/interceptors/auth.interceptor';
import { retryInterceptor } from '@shared/http/interceptors/retry.interceptor';
import { errorInterceptor } from '@shared/http/interceptors/error.interceptor';
import { AppConfigService } from '@shared/config/app-config.service';
import { ThemeService } from '@shared/config/theme.service';
import { LanguageService } from '@shared/config/language.service';
import { RESETTABLE_STORES } from '@shared/config/app-reset.service';
import { NetworkStore } from '@shared/config/network.store';
import { ReferralCaptureService } from '@shared/lib/referral-capture';
import { AuthInterceptorContext } from '@shared/http/auth-interceptor-context';
import { IdleAuthContext } from '@shared/lib/idle/idle-auth-context';
import { AuthStore } from '@features/auth';
import { WalletStore } from '@entities/wallet';
import { BalanceStore } from '@entities/balance';
import { ActivityStore } from '@entities/transaction';
import { OrderStore } from '@entities/order';
import { VaultPositionStore } from '@entities/vault-position';
import { TokenVaultPositionStore } from '@entities/token-vault-position';
import { TradingApi } from '@entities/trading';
import {
  CARD_EVENT_SOURCE,
  CARD_FRESH_TOKEN,
  CARD_FUNDING_SIGNER,
  CARD_TRANSPORT,
  CardStore,
} from '@entities/card';
import { CardHttpTransport } from '@entities/card/api/card.http.transport';
import { CardFundingHttpSigner, CardHttpFreshToken } from '@features/card-funding';
import { CardWsEvents } from '@entities/card/api/card.ws-events';
import { WebWsClient, WsClient } from '@shared/ws';
import { closeDialogsOnNavigation } from '@shared/ui/dialog/close-dialogs-on-navigation';
import { RedeemIdempotencyService } from '@entities/points';
// Deep import, not the `@entities/off-ramp` barrel: the barrel does not
// tree-shake here, and pulling it in drags the whole slice — api, Zod schemas,
// sync service, mappers, SEPA country table — into the initial bundle for
// every session. Measured: 7.5 kB of initial payload for one root store
// reference. The store itself must stay root-provided so `AppResetService`
// clears it on logout.
import { OffRampStore } from '@entities/off-ramp/model/off-ramp.store';
import { PrivateTransferStore } from '@entities/private-transfer/model/private-transfer.store';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),

    provideRouter(
      routes,
      withComponentInputBinding(),
      withViewTransitions(),
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
    ),

    provideHttpClient(
      withFetch(),
      withInterceptors([
        correlationIdInterceptor,
        authInterceptor,
        retryInterceptor,
        errorInterceptor,
      ]),
    ),

    provideTransloco({
      config: {
        availableLangs: ['en', 'ru'],
        defaultLang: 'en',
        fallbackLang: 'en',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),

    provideAppInitializer(() => closeDialogsOnNavigation()),
    provideAppInitializer(() => inject(AppConfigService).load()),
    provideAppInitializer(() => inject(ThemeService).apply()),
    provideAppInitializer(() => inject(LanguageService).apply()),
    provideAppInitializer(async () => {
      const store = inject(AuthStore);
      await store.restoreSession();
      if (!store.refreshToken()) return;
      try {
        await store.refresh();
        await store.loadMe();
      } catch {
        store.forceLogout();
      }
    }),

    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: AuthInterceptorContext, useExisting: AuthStore },
    { provide: IdleAuthContext, useExisting: AuthStore },
    { provide: RESETTABLE_STORES, useExisting: ReferralCaptureService, multi: true },
    { provide: RESETTABLE_STORES, useExisting: NetworkStore, multi: true },
    { provide: RESETTABLE_STORES, useExisting: WalletStore, multi: true },
    { provide: RESETTABLE_STORES, useExisting: BalanceStore, multi: true },
    { provide: RESETTABLE_STORES, useExisting: ActivityStore, multi: true },
    { provide: RESETTABLE_STORES, useExisting: OrderStore, multi: true },
    { provide: RESETTABLE_STORES, useExisting: VaultPositionStore, multi: true },
    { provide: RESETTABLE_STORES, useExisting: TokenVaultPositionStore, multi: true },
    { provide: RESETTABLE_STORES, useExisting: TradingApi, multi: true },
    { provide: RESETTABLE_STORES, useExisting: CardStore, multi: true },
    { provide: RESETTABLE_STORES, useExisting: RedeemIdempotencyService, multi: true },
    { provide: RESETTABLE_STORES, useExisting: OffRampStore, multi: true },
    { provide: RESETTABLE_STORES, useExisting: PrivateTransferStore, multi: true },
    // The web build owns its socket in the UI; the extension swaps in
    // `PortWsClient`, where the background worker owns the only one.
    { provide: WsClient, useExisting: WebWsClient },
    { provide: CARD_EVENT_SOURCE, useExisting: CardWsEvents },
    { provide: CARD_TRANSPORT, useExisting: CardHttpTransport },
    { provide: CARD_FUNDING_SIGNER, useExisting: CardFundingHttpSigner },
    { provide: CARD_FRESH_TOKEN, useExisting: CardHttpFreshToken },
  ],
};
